/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { Tool } from '@/providers/llm-provider'

/**
 * Memory tool for LLM function calling
 * Allows LLMs to store long-term memories in .hivemind/memory.moth
 */

export interface MemoryToolContext {
  currentProject: {
    files: Record<string, string>
  }
  onMemoryAdded?: (memory: string) => Promise<void>
}

/**
 * Get memory tool definition for LLM function calling
 */
export function getMemoryTool(): Tool {
  return {
    type: 'function',
    function: {
      name: 'remember',
      description: 'Store important information in long-term memory. Use this when the user asks you to remember something, or when you learn something important about the project, user preferences, or decisions that should be persisted across sessions.',
      parameters: {
        type: 'object',
        properties: {
          memory: {
            type: 'string',
            description: 'The information to remember. Should be concise but complete. Include context about what this relates to.',
          },
          category: {
            type: 'string',
            enum: ['user_preference', 'project_context', 'technical_decision', 'important_note'],
            description: 'Category of the memory for organization',
          },
        },
        required: ['memory', 'category'],
      },
    },
  }
}

/**
 * Execute the remember tool
 * Appends the memory to .hivemind/memory.moth
 */
export async function executeRememberTool(
  args: { memory: string; category: string },
  context: MemoryToolContext
): Promise<string> {
  const { memory, category } = args
  const { currentProject, onMemoryAdded } = context

  try {
    // Get current memory file (try .moth first, fallback to .md)
    const mothPath = '.hivemind/memory.moth'
    const mdPath = '.hivemind/memory.md'
    const memoryPath = currentProject.files[mothPath] ? mothPath : mdPath

    let memoryContent = currentProject.files[memoryPath] || `intent:project_memory
audience:ai_agents; version:1.0.0

[PRODUCT]
name:${(currentProject as any).name || 'Project'}

[USER_PREFERENCES]
# User preferences will be added here

[ARCHITECTURE_DECISIONS]
# Architecture decisions will be added here

[IMPORTANT_CONTEXT]
# Important context will be added here

[TECHNICAL_NOTES]
# Technical notes will be added here
`

    // Determine which section to add to based on category
    const sectionMap: Record<string, string> = {
      user_preference: '[USER_PREFERENCES]',
      project_context: '[PRODUCT]',
      technical_decision: '[ARCHITECTURE_DECISIONS]',
      important_note: '[IMPORTANT_CONTEXT]',
    }

    const sectionHeader = sectionMap[category] || '[IMPORTANT_CONTEXT]'

    // Find the section and append the memory
    const timestamp = new Date().toISOString().split('T')[0]
    const memoryEntry = `${timestamp}:${memory.replace(/:/g, '=').replace(/\n/g, ' ')}`

    // Find the section and insert after it
    const sectionIndex = memoryContent.indexOf(sectionHeader)
    if (sectionIndex !== -1) {
      // Find the next section or end of file
      const afterSection = memoryContent.substring(sectionIndex + sectionHeader.length)
      const nextSectionMatch = afterSection.match(/\n\[/)
      const insertPosition = nextSectionMatch
        ? sectionIndex + sectionHeader.length + afterSection.indexOf(nextSectionMatch[0])
        : memoryContent.length

      // Insert the memory
      memoryContent =
        memoryContent.substring(0, insertPosition) +
        '\n' + memoryEntry +
        memoryContent.substring(insertPosition)
    } else {
      // Section doesn't exist, append at end
      memoryContent += `\n\n${sectionHeader}\n${memoryEntry}\n`
    }

    // Call the callback to update the file
    if (onMemoryAdded) {
      await onMemoryAdded(memoryContent)
    }

    return `✓ Memory stored: "${memory}" (category: ${category})\n\nThis will be remembered across sessions and available to all AI assistants working on this project.`
  } catch (error) {
    console.error('[MemoryTool] Failed to store memory:', error)
    return `✗ Failed to store memory: ${error instanceof Error ? error.message : 'Unknown error'}`
  }
}

